/* eslint-disable no-underscore-dangle */
/* eslint-disable import/no-unresolved */
import '@honda-canada/design-system-react/lib/psp-css.css';
import '@splidejs/splide/dist/css/themes/splide-default.min.css';
import 'core-js/stable';
import 'es6-promise/auto';
import 'isomorphic-fetch';

import { PSPTheme as Theme } from '@honda-canada/design-system-react/lib/psp-theme';
import AppRootWithUserLocation from '@honda-canada/sitecore-jss-common/lib/components/AppRootWithUserLocation';
import ErrorBoundary from '@honda-canada/sitecore-jss-common/lib/components/ErrorBoundary';
import i18nInit from '@honda-canada/sitecore-jss-common/lib/utils/i18nInit';
import isBrowserModern from '@honda-canada/sitecore-jss-common/lib/utils/isBrowserModern';
import BottomElementProvider from '@honda-canada/sitecore-jss-components/lib/components/Footer/service/BottomElementProvider';
import LocalStorageProvider from '@honda-canada/sitecore-jss-components/lib/components/LocalStorageContext';
import ModelExplorationContextProvider from '@honda-canada/sitecore-jss-components/lib/components/ModelExplorationContext';
import ModelFiltersContextProvider from '@honda-canada/sitecore-jss-components/lib/components/ModelFiltersContext';
import PopupContextProvider from '@honda-canada/sitecore-jss-components/lib/components/Popup/PopupContext';
import ReducersProvider from '@honda-canada/sitecore-jss-components/lib/components/ReducersProvider';
import { store } from '@honda-canada/sitecore-jss-components/lib/core/store';
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import TagManager from 'react-gtm-module';
import { HelmetProvider } from 'react-helmet-async';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import MainContainer from './MainContainer';

import ApplicationErrorMessage from './ApplicationErrorMessage';
import componentFactory from './componentFactory';
import updateLanguageLink from './updateLanguageLinks';

let renderFunction = ReactDOM.render;

let initLanguage = 'en';

let __JSS_STATE__ = null;
const ssrRawJson = document.getElementById('__JSS_STATE__');

if (ssrRawJson) {
  __JSS_STATE__ = JSON.parse(ssrRawJson.innerHTML);
}

if (__JSS_STATE__) {
  renderFunction = ReactDOM.hydrate;
  initLanguage = __JSS_STATE__.sitecore.context.language;
}

const { config } = __JSS_STATE__;

const gtmDevParameters = new URLSearchParams(config.gtmDevParameters);
const tagManagerArgs = config.gtmDevParameters
  ? {
      gtmId: 'GTM-TZXJ8LH',
      auth: gtmDevParameters && gtmDevParameters.get('gtm_auth'),
      preview: gtmDevParameters && gtmDevParameters.get('gtm_preview'),
    }
  : {
      gtmId: 'GTM-TZXJ8LH',
    };

TagManager.initialize(tagManagerArgs);

const App = () => {
  const [reloadLocationFromCookies, setReloadLocationFromCookies] = useState(false);

  return (
    <ErrorBoundary errorMessage={<ApplicationErrorMessage />}>
      <ReduxProvider store={store}>
        <HelmetProvider>
          <AppRootWithUserLocation
            path={window.location.pathname}
            Router={BrowserRouter}
            ReducersProvider={ReducersProvider}
            ssrState={__JSS_STATE__}
            updateExternalLanguageLinks={(language, routeLanguageSelectors) => {
              updateLanguageLink('#toggleUrl', language, routeLanguageSelectors);
              updateLanguageLink('.global-header-compressed-link.language', language, routeLanguageSelectors);
            }}
            config={config}
            errorMessage={<ApplicationErrorMessage is404 />}
            activePlaceholders={['main', 'footer', 'header']}
            modelFiltersContextProvider={ModelFiltersContextProvider}
            modelExplorationContextProvider={ModelExplorationContextProvider}
            bottomElementProvider={BottomElementProvider}
            localStorageProvider={LocalStorageProvider}
            reloadLocationFromCookies={reloadLocationFromCookies}
            setReloadLocationFromCookies={setReloadLocationFromCookies}
            popupContextProvider={PopupContextProvider}
            theme={Theme}
            mainContainer={MainContainer}
            componentFactory={componentFactory}
          />
        </HelmetProvider>
      </ReduxProvider>
    </ErrorBoundary>
  );
};

const renderApp = async () => {
  const dictionary = __JSS_STATE__?.viewBag?.dictionary;
  const rootElement = document.getElementById('jss-root');

  await i18nInit(initLanguage, dictionary, config);

  renderFunction(<App />, rootElement);
};

if (isBrowserModern()) {
  renderApp();
} else {
  import('./polyfills').then(renderApp);
}
